import React from 'react';
import styled from 'styled-components';
import {FlexRow, tokensForThemeProvider} from '@forecast-it/design-system';
import {ConditionsEditor} from './conditions/ConditionsEditor';
import {GroupByControl} from './GroupByControl';
import {ColumnManager} from './ColumnManager';

const tokens = tokensForThemeProvider.sky.sys;

/** Bleeds past `SurfaceWrapper`'s 32px horizontal page padding (`ReportBuilderSurface.tsx`) so this
 * toolbar's background/border reach the viewport edges, then reapplies that padding internally so
 * its own content still lines up with the rest of the page. */
const ToolbarWrapper = styled.div`
	margin: 0 -${tokens.spacing['800']};
	padding: ${tokens.spacing['400']} ${tokens.spacing['800']};
	background: ${tokens.color.background.neutral['2']};
	border-top: 1px solid ${tokens.color.border.decorative};
	border-bottom: 1px solid ${tokens.color.border.decorative};
`;

/** Pins this block to the right edge of the toolbar even when `ConditionsEditor` renders nothing
 * (no sibling to space away from) — `margin-left: auto` on a flex item pushes itself (and anything
 * after it) to the end of the flex container regardless of how many siblings precede it. `FlexRow`
 * doesn't forward a `style` prop, so this composes via `className` instead. */
const RightAlignedFlexRow = styled(FlexRow)`
	margin-left: auto;
`;

/** One bordered toolbar strip holding the filter conditions on the left and Group By + Columns on
 * the right. `ConditionsEditor` already wraps onto additional rows as more conditions are added
 * (its own `wrap={'wrap'}`); the right-hand block is `flex: 0 0 auto` so it never shrinks or moves
 * as the left side grows taller — it stays pinned at the top-right of the toolbar regardless of how
 * many rows the conditions area wraps to. `ConditionsEditor` renders nothing until the field
 * taxonomy has loaded (or if it fails to load), so the right-hand block uses `RightAlignedFlexRow`
 * to stay pinned right even as the toolbar's only visible child. */
export function ReportToolbar(): JSX.Element {
	return (
		<ToolbarWrapper data-cy={'ai-report-toolbar'}>
			<FlexRow justifyContent={'space-between'} alignItems={'flex-start'} gap={'xl'}>
				<ConditionsEditor />
				<RightAlignedFlexRow gap={'xl'} justifyContent={'flex-end'} alignItems={'flex-end'} flex={'0 0 auto'}>
					<GroupByControl />
					<ColumnManager />
				</RightAlignedFlexRow>
			</FlexRow>
		</ToolbarWrapper>
	);
}
